import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { COMPANY, formatEffectiveDate } from './companyInfo';
import logoUrl from '@/assets/logo-horizontal.png';

// Shared shell for /terms and /privacy. Brand-aligned light theme per
// the Pro Haul Brand Style Guide: white background, prohaul-black
// text, prohaul-red accents on hover links. Mirrors CarrierAuth +
// AcceptInvite so the entire public surface is consistently branded.
//
// Public route (no auth gate) so Stripe verification, search engines,
// and unauthenticated visitors can all reach it.
export default function LegalShell({ title, children }) {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-prohaul-white text-prohaul-black">
      {/* Header */}
      <header className="border-b border-prohaul-black/10">
        <div className="max-w-3xl mx-auto px-4 py-5 flex items-center justify-between gap-4">
          {/* Bare logo on the white header bar. h-8 keeps it compact. */}
          <Link to="/" aria-label="Pro Haul home" className="inline-flex items-center">
            <img
              src={logoUrl}
              alt="Pro Haul"
              className="h-8 w-auto block"
            />
          </Link>
          <button
            onClick={() => navigate(-1)}
            className="text-xs text-prohaul-black/60 hover:text-prohaul-red inline-flex items-center gap-1"
          >
            <ArrowLeft className="w-3 h-3" /> Back
          </button>
        </div>
      </header>

      {/* Body */}
      <main className="max-w-3xl mx-auto px-4 py-10 space-y-6">
        <div>
          <h1 className="text-3xl font-stencil font-bold text-prohaul-black tracking-wide">
            {title}
          </h1>
          <p className="text-xs text-prohaul-black/60 mt-1">
            Effective {formatEffectiveDate()}
          </p>
        </div>

        <article className="space-y-5 text-sm leading-relaxed text-prohaul-black/85">
          {children}
        </article>
      </main>

      {/* Footer */}
      <footer className="border-t border-prohaul-black/10 mt-12">
        <div className="max-w-3xl mx-auto px-4 py-6 flex flex-wrap items-center justify-between gap-3 text-xs text-prohaul-black/60">
          <div>
            © {new Date().getFullYear()} {COMPANY.legalName}. All rights reserved.
          </div>
          <div className="flex items-center gap-4">
            <Link to="/terms"   className="hover:text-prohaul-red">Terms of Service</Link>
            <Link to="/privacy" className="hover:text-prohaul-red">Privacy Policy</Link>
            <a
              href={`mailto:${COMPANY.supportEmail}`}
              className="hover:text-prohaul-red"
            >
              Contact
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}

// Convenience helpers for consistent in-body styling without pulling
// in @tailwindcss/typography. Brand-aligned: prohaul-black for headings
// and copy, prohaul-black/10 for thin dividers under H2 sections.
export const H2 = ({ children }) => (
  <h2 className="text-lg font-stencil font-bold text-prohaul-black mt-8 mb-2 border-b border-prohaul-black/10 pb-1.5 tracking-wide">
    {children}
  </h2>
);

export const H3 = ({ children }) => (
  <h3 className="text-base font-semibold text-prohaul-black mt-4 mb-1.5">{children}</h3>
);

export const P = ({ children }) => (
  <p className="text-prohaul-black/85">{children}</p>
);

export const UL = ({ children }) => (
  <ul className="list-disc pl-5 space-y-1.5 text-prohaul-black/85 marker:text-prohaul-black/40">
    {children}
  </ul>
);
